// ╔══════════════════════════════════════════════════════════════╗
// ║  App router — protected routes per role                       ║
// ╚══════════════════════════════════════════════════════════════╝

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth.jsx';
import Layout from './components/Layout.jsx';
import LoginPage from './pages/Login.jsx';
import DashboardPage from './pages/Dashboard.jsx';
import EmployeesPage from './pages/Employees.jsx';
import ProfilePage from './pages/Profile.jsx';
import PayslipsPage from './pages/Payslips.jsx';
import PayslipDetailPage from './pages/PayslipDetail.jsx';
import UploadsPage from './pages/Uploads.jsx';
import FingerprintUploadPage from './pages/FingerprintUpload.jsx';
import AttendancePage from './pages/Attendance.jsx';
import PayrollPage from './pages/Payroll.jsx';
import ReportsPage from './pages/Reports.jsx';
import SettingsPage from './pages/Settings.jsx';
import AuditPage from './pages/Audit.jsx';
import ChangePasswordPage from './pages/ChangePassword.jsx';
import ProductionEntryPage from './pages/production/Entry.jsx';
import ProductionWorkersPage from './pages/production/Workers.jsx';
import ProductionStagesPage from './pages/production/Stages.jsx';
import WorkerMonthlyPage from './pages/production/WorkerMonthly.jsx';
import ProductionHistoryPage from './pages/production/History.jsx';
import ProductionReviewPage from './pages/production/Review.jsx';

function Protected({ allow, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink-50 dark:bg-ink-950">
        <div className="w-12 h-12 rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allow && !allow.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// `att` role is single-page — always lands on /fingerprint.
function RoleHome() {
  const { user } = useAuth();
  if (user?.role === 'att') return <Navigate to="/fingerprint" replace />;
  return <DashboardPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />

      <Route element={<Protected><Layout /></Protected>}>
        <Route
          path="/"
          element={
            <RoleHome />
          }
        />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route
          path="/fingerprint"
          element={
            <Protected allow={['admin', 'acc', 'att']}>
              <FingerprintUploadPage />
            </Protected>
          }
        />

        {/* HR — admin & acc */}
        <Route
          path="/employees"
          element={
            <Protected allow={['admin', 'acc']}>
              <EmployeesPage />
            </Protected>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <Protected allow={['admin', 'acc', 'eng']}>
              <ProfilePage />
            </Protected>
          }
        />
        <Route
          path="/attendance"
          element={
            <Protected allow={['admin', 'acc']}>
              <AttendancePage />
            </Protected>
          }
        />
        <Route
          path="/payroll"
          element={
            <Protected allow={['admin', 'acc']}>
              <PayrollPage />
            </Protected>
          }
        />
        <Route
          path="/payslips"
          element={
            <Protected allow={['admin', 'acc']}>
              <PayslipsPage />
            </Protected>
          }
        />
        <Route
          path="/payslips/:id"
          element={
            <Protected allow={['admin', 'acc']}>
              <PayslipDetailPage />
            </Protected>
          }
        />
        <Route
          path="/uploads"
          element={
            <Protected allow={['admin', 'acc']}>
              <UploadsPage />
            </Protected>
          }
        />

        {/* Reports — all roles */}
        <Route path="/reports" element={<ReportsPage />} />

        {/* Production — eng & admin (and review for acc) */}
        <Route
          path="/production/entry"
          element={
            <Protected allow={['admin', 'eng']}>
              <ProductionEntryPage />
            </Protected>
          }
        />
        <Route
          path="/production/workers"
          element={
            <Protected allow={['admin', 'eng']}>
              <ProductionWorkersPage />
            </Protected>
          }
        />
        <Route
          path="/production/workers/:id/monthly"
          element={
            <Protected allow={['admin', 'eng', 'acc']}>
              <WorkerMonthlyPage />
            </Protected>
          }
        />
        <Route
          path="/production/stages"
          element={
            <Protected allow={['admin', 'eng']}>
              <ProductionStagesPage />
            </Protected>
          }
        />
        <Route
          path="/production/history"
          element={
            <Protected allow={['admin', 'eng', 'acc']}>
              <ProductionHistoryPage />
            </Protected>
          }
        />
        <Route
          path="/production/review"
          element={
            <Protected allow={['admin', 'acc']}>
              <ProductionReviewPage />
            </Protected>
          }
        />

        {/* Admin only */}
        <Route
          path="/audit"
          element={
            <Protected allow={['admin']}>
              <AuditPage />
            </Protected>
          }
        />

        <Route path="/settings" element={<SettingsPage />} />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="p-8 text-center">
              <h2 className="text-2xl font-black">الصفحة غير موجودة</h2>
              <p className="text-ink-400 mt-2">المسار اللي طلبته مش متاح.</p>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
